/**
 * How a reminder rule reads to an organizer (SPEC §8.18, MT-156).
 *
 * The server stores kinds, audiences and whole-day offsets; the editor says
 * "RSVP reminder · 4 days before at 6:00 PM · households that haven't
 * answered". One place for the wording, so the settings screen and a meal's
 * dashboard describe the same rule in the same words.
 */

const Kinds: Record<string, string> = {
  Rsvp: 'RSVP reminder',
  StillNeeded: 'Still-needed reminder',
  Contribution: 'Contribution reminder',
  EventDay: 'Event-day reminder',
}

const KindHints: Record<string, string> = {
  Rsvp: "Asks households that haven't said whether they're coming.",
  StillNeeded: 'Lists what nobody has signed up to bring yet.',
  Contribution: 'Reminds households what they said they would bring.',
  EventDay: 'A note on the day itself.',
}

/** Every audience a standing rule can have. Hand-picked is for one message, not a rule. */
export const RuleAudiences = [
  { value: 'Everyone', title: 'Every household' },
  { value: 'Unanswered', title: "Households that haven't answered" },
  { value: 'Maybe', title: 'Households that said maybe' },
  { value: 'Contributing', title: 'Households bringing something' },
  { value: 'NotContributing', title: 'Households not bringing anything' },
] as const

export function kindLabel(kind: string): string {
  return Kinds[kind] ?? kind
}

export function kindHint(kind: string): string {
  return KindHints[kind] ?? ''
}

export function audienceLabel(audience: string): string {
  return RuleAudiences.find((a) => a.value === audience)?.title ?? audience
}

/** `18:00` → `6:00 PM`, in the reader's own clock style. */
export function clockTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number]
  const at = new Date(2000, 0, 1, h, m)
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

/** "4 days before at 6:00 PM", "1 day before at 5:00 PM", "On the day at 9:00 AM". */
export function timingLine(daysBefore: number, localTime: string): string {
  const at = clockTime(localTime)
  if (daysBefore === 0) return `On the day at ${at}`
  return `${daysBefore} ${daysBefore === 1 ? 'day' : 'days'} before at ${at}`
}
